/**
 * RelationalReportsPage — entry point for cross-entity reports.
 *
 * MVP ships ONE report (Top Spending Fans). The 7 follow-up reports listed in
 * docs/REPORTS.md are stubbed as "Coming soon" tiles so the IA is correct
 * from day one and adding each one is a 30-minute follow-up PR.
 */
import React, { useState } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { TopSpendersReport } from "./TopSpendersReport";
import { TopEarningCreatorsReport } from "./TopEarningCreatorsReport";
import { BrandCampaignRoiReport } from "./BrandCampaignRoiReport";
import { MonthlyReport } from "./MonthlyReport";

const REPORT_CATALOG = [
  // Active
  { id: "monthly-report",        title: "Monthly Report",          summary: "13 core KPIs by month — registrations, calls, money, reviews, campaigns. Excel export, custom fields.", accent: "#FC8019", status: "live" },
  { id: "top-spenders",          title: "Top Spending Fans",       summary: "Whales by total spend, with cross-creator concentration.", accent: "#FC8019", status: "live" },
  { id: "top-earners",           title: "Top Earning Creators",    summary: "Earnings within window — call vs brand mix, withdrawn rate.", accent: "#4CAF50", status: "live" },
  { id: "brand-campaign-roi",    title: "Brand Campaign ROI",      summary: "Per-campaign budget vs delivered earnings, fulfillment, CTR.", accent: "#7C4DFF", status: "live" },
  // Queued
  { id: "concentration-risk",    title: "Concentration-Risk Creators", summary: "Single fan = >50% of creator's revenue (churn risk).", accent: "#FF6B9D", status: "queued" },
  { id: "dormant-whales",        title: "Dormant Whales",          summary: "High lifetime value, no call in 30+ days.",                accent: "#7C4DFF", status: "queued" },
  { id: "top-brand-spenders",    title: "Top Spending Brands",     summary: "GMV, # campaigns, # creators worked with.",                accent: "#FF6B9D", status: "queued" },
  { id: "underused-budgets",     title: "Underused Brand Budgets", summary: "High wallet, low recent campaign activity.",               accent: "#FFC107", status: "queued" },
  { id: "refund-heavy",          title: "Refund-Heavy Fans",       summary: "Highest refund rate (potential abuse).",                   accent: "#FF4757", status: "queued" },
  { id: "one-call-wonders",      title: "One-Call Wonders",        summary: "Paid once, never came back. D1 retention by spend.",       accent: "#2EC4B6", status: "queued" },
];

function ReportCard({ report, active, onSelect }) {
  const isLive = report.status === "live";
  return (
    <button
      type="button"
      onClick={() => isLive && onSelect(report.id)}
      disabled={!isLive}
      style={{
        background: active === report.id ? T.surface.cardHover : T.surface.card,
        border: `1px solid ${active === report.id ? report.accent : T.border.subtle}`,
        borderLeft: `4px solid ${report.accent}`,
        borderRadius: 12,
        padding: 14,
        textAlign: "left",
        cursor: isLive ? "pointer" : "not-allowed",
        opacity: isLive ? 1 : 0.55,
        fontFamily: T.font.sans,
        outline: "none",
        display: "flex", flexDirection: "column", gap: 6, minHeight: 92,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>{report.title}</div>
        {!isLive && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            color: T.text.muted, padding: "3px 8px", borderRadius: 999,
            background: T.surface.canvas, border: `1px solid ${T.border.subtle}`,
          }}>Soon</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: T.text.muted, lineHeight: 1.4 }}>{report.summary}</div>
    </button>
  );
}

export function RelationalReportsPage() {
  const [activeReport, setActiveReport] = useState("monthly-report");

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>Relational reports</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>
          Pre-canned cross-entity queries. Each report answers one specific business question. Click any tile to open it.
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
        gap: 10, marginBottom: 14,
      }}>
        {REPORT_CATALOG.map(r => (
          <ReportCard key={r.id} report={r} active={activeReport} onSelect={setActiveReport} />
        ))}
      </div>

      {activeReport === "monthly-report" && <MonthlyReport />}
      {activeReport === "top-spenders" && <TopSpendersReport />}
      {activeReport === "top-earners" && <TopEarningCreatorsReport />}
      {activeReport === "brand-campaign-roi" && <BrandCampaignRoiReport />}
    </>
  );
}
